import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { TasasRepository } from '@app/domain/ports/tpv/tasas.repository';
import {
  TASA_EJES,
  TasaEje,
  TasaPatchBody,
  TasaPatchResponse,
  TasaRead,
  TasasByEje,
} from '@app/data/dtos/tpv/tasas.dto';

/**
 * Mock de `/tasa/` para pruebas sin backend (toggle `tpvUseMocks`).
 *
 * GET arranca con los 4 ejes en `null` (nodo sin capturar → UI en 0 %). El
 * PATCH echa las claves recibidas al mapa, calcula un `tasas_anteriores`
 * coherente (partiendo de `null`s) y devuelve `push_micro.skipped: true` para
 * el nivel EMPRESA — así el contenedor ejerce el aviso de "matriz de comisiones
 * pendiente" (§4, nota de alta).
 */
@Injectable()
export class TasasMockDataSource extends TasasRepository {
  private readonly emptyTasas: TasasByEje = Object.freeze({
    credito: null,
    debito: null,
    internacional: null,
    amex: null,
  });

  override getCompanyTasa(_idCompany: string): Observable<TasaRead | null> {
    return of({ nivel: 'empresa', tasas: { ...this.emptyTasas } } satisfies TasaRead).pipe(
      delay(250),
    );
  }

  override updateCompanyTasa(_idCompany: string, body: TasaPatchBody): Observable<TasaPatchResponse> {
    return of(this.buildPatchResponse(body, { pushMicroSkipped: true })).pipe(delay(400));
  }

  override getUnitTasa(_idCompany: string, _idUnit: string): Observable<TasaRead | null> {
    return of({ nivel: 'unidad', tasas: { ...this.emptyTasas } } satisfies TasaRead).pipe(
      delay(250),
    );
  }

  override updateUnitTasa(
    _idCompany: string,
    _idUnit: string,
    body: TasaPatchBody,
  ): Observable<TasaPatchResponse> {
    return of(this.buildPatchResponse(body)).pipe(delay(400));
  }

  override getMerchantTasa(_idMerchant: string): Observable<TasaRead | null> {
    return of({
      nivel: 'comercio',
      tasas: { ...this.emptyTasas },
      sync_pendiente: false,
    } satisfies TasaRead).pipe(delay(250));
  }

  override updateMerchantTasa(_idMerchant: string, body: TasaPatchBody): Observable<TasaPatchResponse> {
    return of(this.buildPatchResponse(body)).pipe(delay(400));
  }

  private buildPatchResponse(
    body: TasaPatchBody,
    opts: { pushMicroSkipped?: boolean } = {},
  ): TasaPatchResponse {
    const anteriores: TasasByEje = { ...this.emptyTasas };
    const nuevas: TasasByEje = { ...anteriores };
    for (const eje of TASA_EJES) {
      const val = body.tasas?.[eje as TasaEje];
      if (val == null) continue;
      nuevas[eje] = val.toFixed(2);
    }
    return {
      tasas_anteriores: anteriores,
      tasas: nuevas,
      propagados: [],
      sync_marcados: [],
      ...(opts.pushMicroSkipped
        ? { push_micro: { skipped: true, reason: 'sin_matriz_comisiones' } }
        : {}),
    };
  }
}
